/**
 * The envelope page (TMMAPP-0006, design §14.4 row 2; rebuilt to prototype v2 by TMMAPP-0101, audit 06
 * § B1–B4, v2 L1298–1640) at `/v/:virtualAccountId/e/:envelopeId`.
 *
 * - Header (B1): the back link by origin — "← Virtual account", or "← Back to account" when the route state
 *   says `{ from: 'account', accountId }` — the `h1` with its kebab (always shown), the share line from the
 *   virtual account's roles, the narrative per kind, and the right headline "Current balance" with
 *   "{in} in · {out} out" and "APR {x} · reference only".
 * - "Categories in this envelope" (B2), then a BUDGET envelope's BUDGET card (TMMAPP-0111, MM-D-118: the
 *   dated budgets and "Change budget"), and the Notes card (served `notes`, whoever is served one).
 * - Tabs (B3) Register · Recurring · Cashflow forecast, then (D1) P&L, synced to `?tab=`; the period
 *   selector lives in the Register and P&L cards' toolbars and the workbook export is "Download overview"
 *   in the kebab, for the same window: the virtual account P&L's calendar presets, Year to date by
 *   default (TMMAPP-0114 A3).
 * - The kebab (B4): a writer — Edit, Permissions (owner only; the virtual account's Sharing); a reader —
 *   Shared with you; then (D1) Alerts, Analyze (owner only, MM-D-47), Download overview, Move money…, Move….
 *
 * Roles (TMMAPP-0110): every control reads the served standing through `useVirtualAccountRole()`. Writers
 * are the served `canManage` (the owner or a Manager); Permissions, Move… and Analyze stay the owner's. In
 * the owner's GRACE the page is read-only under the grace notice; a LOCKED owner's shows the locked
 * state. Money renders in the virtual account OWNER's currency from `/me` (§14.1); a virtual account
 * absent from `/me` (archived, or a stale link) is a not-found page, as is a 404 (MM-D-05).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categoriesApi, type Category } from '@/api/categories';
import { isApiError, isNotFound, standingRefusal } from '@/api/errors';
import { envelopesApi, type Envelope } from '@/api/envelopes';
import type { MeVirtualAccount } from '@/api/me';
import { virtualAccountsApi, type VirtualAccountDetail } from '@/api/virtualAccounts';
import { ErrorState } from '@/components/ErrorState';
import { ENVELOPE_KIND_TINT } from '@/components/envelopeKindTint';
import { TintChip } from '@/components/TintChip';
import { AskAboutThis } from '@/features/assistant/AskAboutThis';
import { AnalysisDialog } from '@/features/ai/AnalysisDialog';
import { AlertsDialog } from '@/features/envelope/alerts/AlertsDialog';
import { canSeeAlerts, canWriteAlerts } from '@/features/envelope/alerts/canSeeAlerts';
import { sponsorName } from '@/features/onboarding/sponsorName';
import { SubscribeCard } from '@/features/subscribe/SubscribeCard';
import { EnvelopeFormDialog } from '@/features/virtualAccount/EnvelopeFormDialog';
import { InfoDialog } from '@/features/virtualAccount/InfoDialog';
import { MoveEnvelopeDialog } from '@/features/virtualAccount/MoveEnvelopeDialog';
import { MoveMoneyDialog } from '@/features/virtualAccount/MoveMoneyDialog';
import { GraceNotice, VirtualAccountLockedBody } from '@/features/virtualAccount/OwnerAccessNotice';
import { SharingDialog } from '@/features/virtualAccount/SharingDialog';
import { useOptionalVirtualAccountsList } from '@/features/virtualAccounts/virtualAccountsListContext';
import { nextSortOrder, orderEnvelopes } from '@/features/virtualAccount/envelopeOrder';
import { parseRouteId } from '@/features/virtualAccount/routeId';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { NotFoundBody, RecordNotFound } from '@/routes/NotFound';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { useVirtualAccountRole } from '@/session/useVirtualAccountRole';
import { enumParam, useUrlState } from '@/shared/useUrlState';
import { useNotify } from '@/shell/NotifyProvider';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { downloadBlob } from '@/utils/downloadBlob';
import { slug } from '@/utils/slug';
import { BudgetHistoryCard } from './BudgetHistoryCard';
import { CategoriesInEnvelopeCard, NotesCard } from './EnvelopeCards';
import { EnvelopeForecastTab } from './EnvelopeForecastTab';
import { envelopeNarrative, hasCategoryTotals } from './envelopeCopy';
import { EnvelopeOverview } from './EnvelopeOverview';
import { PlanLinesPanel } from './PlanLinesPanel';
import { PnlTab } from './PnlTab';
import { RegisterTab } from './RegisterTab';
import { EnvelopeRangeControl } from './EnvelopeRangeControl';
import { rangeDates, type EnvelopeRange } from './envelopeRange';
import { useEnvelopeRange } from './useEnvelopeRange';

type EnvelopeState =
  | { status: 'loading' }
  | {
      status: 'ready';
      envelope: Envelope;
      /** The virtual account's detail (shares, envelopes); `null` when it could not be read. */
      detail: VirtualAccountDetail | null;
      /** Every category of the virtual account; `null` falls back to the envelope's own list. */
      categories: Category[] | null;
    }
  | { status: 'notFound' }
  /** The owner is locked (`403 VIRTUAL_ACCOUNT_LOCKED`, MM-D-116). */
  | { status: 'locked' }
  | { status: 'error'; error: unknown };

type TabKey = 'register' | 'recurring' | 'cashflow' | 'pnl';
type DialogState =
  | { kind: 'subscribe' }
  | { kind: 'analyze' }
  | { kind: 'alerts' }
  | { kind: 'edit' }
  | { kind: 'sharing' }
  | { kind: 'sharedWithYou' }
  | { kind: 'moveMoney' }
  | { kind: 'move' }
  | null;

const TABS: readonly TabKey[] = ['register', 'recurring', 'cashflow', 'pnl'];
const URL_SCHEMA = { tab: enumParam<TabKey>(TABS, 'register') };

/** The account the envelope was opened from (`{ from: 'account', accountId }`), if the route says so. */
function originAccountId(state: unknown): number | null {
  if (typeof state !== 'object' || state === null) return null;
  const { from, accountId } = state as { from?: unknown; accountId?: unknown };
  return from === 'account' && typeof accountId === 'number' ? accountId : null;
}

function EnvelopeDetailView({
  virtualAccountId,
  envelopeId,
}: {
  virtualAccountId: number;
  envelopeId: number;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const virtualAccountsList = useOptionalVirtualAccountsList();
  const navigate = useNavigate();
  const location = useLocation();
  const [{ tab }, setUrl] = useUrlState(URL_SCHEMA);
  const [state, setState] = useState<EnvelopeState>({ status: 'loading' });
  const [envelopeRange, setEnvelopeRange] = useEnvelopeRange();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [version, setVersion] = useState(0);
  const notify = useNotify();
  // StrictMode double-invokes the mount effect; the request is issued once per id.
  const started = useRef(false);

  /** Reads the page; resolves to whether the virtual account's detail was read. */
  const read = useCallback(async (): Promise<boolean> => {
    try {
      // The virtual account's detail and categories ride along: the share line, Sharing and Move money
      // read the one, the register's category picker the other. Neither failing blocks the page.
      const [envelope, detail, categories] = await Promise.all([
        envelopesApi.get(envelopeId),
        virtualAccountsApi.get(virtualAccountId).catch(() => null),
        categoriesApi.list(virtualAccountId).catch(() => null),
      ]);
      // A failed re-read keeps the detail already on screen, so an open dialog that reads it (Sharing,
      // Move money) never silently closes (TMMAPP-0160 item 7).
      setState((prev) => ({
        status: 'ready',
        envelope,
        detail: detail ?? (prev.status === 'ready' ? prev.detail : null),
        categories,
      }));
      // The overview re-reads its register, plan lines and items after every reload (a plan line
      // edited in Recurring, money moved).
      setVersion((n) => n + 1);
      return detail !== null;
    } catch (error) {
      const next: EnvelopeState =
        isApiError(error) && isNotFound(error)
          ? { status: 'notFound' }
          : standingRefusal(error) === 'locked'
            ? { status: 'locked' }
            : { status: 'error', error };
      // A failed re-fetch after a mutation keeps the last good detail on screen — except a LOCKED
      // owner, whose contents are refused from now on.
      setState((prev) => (prev.status === 'ready' && next.status !== 'locked' ? prev : next));
      return false;
    }
  }, [envelopeId, virtualAccountId]);
  const load = useCallback(async () => {
    await read();
  }, [read]);

  // The caller's served standing (TMMAPP-0110), from `/me` and the virtual account's detail.
  const role = useVirtualAccountRole(
    virtualAccountId,
    state.status === 'ready' ? state.detail : null,
  );
  // A LOCKED owner's virtual account is listed but its contents are refused: no read.
  const lockedByOwner = role.ownerAccess === 'LOCKED';

  useEffect(() => {
    if (started.current || lockedByOwner) return;
    started.current = true;
    void load();
  }, [load, lockedByOwner]);

  // The virtual account's name and its OWNER's currency come from `/me` (money renders in the owner's
  // currency, §14.1); the envelope route carries the virtual account id.
  const virtualAccount = me.virtualAccounts.find((d) => d.id === virtualAccountId) ?? null;
  const envelope = state.status === 'ready' ? state.envelope : null;
  const absent = virtualAccount === null || state.status === 'notFound';
  const locked = lockedByOwner || state.status === 'locked';
  const title = usePageTitle({
    title:
      envelope?.name ??
      (locked ? virtualAccount?.name : null) ??
      (absent
        ? t('notFound.title')
        : state.status === 'error'
          ? t('pageHeader.fallback.envelope')
          : null),
    fallback: t('pageHeader.fallback.envelope'),
  });

  // Every state renders the same `PageHeader` first, so the `h1` (and route focus on it) survives
  // loading → ready / error / not found.
  if (virtualAccount === null || state.status === 'notFound') {
    return (
      <>
        <PageHeader title={title} />
        <NotFoundBody />
      </>
    );
  }

  // "← Virtual account", to the envelope's virtual account; "← Back to account" from an account (B1).
  const accountOrigin = originAccountId(location.state);
  const back =
    accountOrigin !== null
      ? { label: t('envelope.backToAccount'), to: `/me/accounts/${accountOrigin}` }
      : { label: virtualAccount.name, to: `/v/${virtualAccountId}` };

  if (locked) {
    return (
      <>
        <PageHeader title={title} back={back} />
        <VirtualAccountLockedBody
          owner={
            sponsorName(me, virtualAccountId) || personName(virtualAccount.ownerDisplayName, t)
          }
        />
      </>
    );
  }

  if (state.status === 'loading') {
    return (
      <>
        <PageHeader title={title} back={back} />
        <Skeleton variant="rounded" height={120} />
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <PageHeader title={title} back={back} />
        <ErrorState
          error={state.error}
          what={t('errors.what.envelope')}
          onRetry={() => {
            setState({ status: 'loading' });
            void load();
          }}
        />
      </>
    );
  }

  const row = state.envelope;
  const detail = state.detail;
  const categories = state.categories ?? row.categories ?? [];
  const owner = role.isOwner;
  // Writers: the served `canManage` (the owner or a Manager); owner-only stays the owner's.
  const canWrite = role.canWrite && detail?.status !== 'ARCHIVED';
  const ownerActions = owner && detail?.status !== 'ARCHIVED';
  const currency = virtualAccount.currency;
  const sponsor =
    sponsorName(me, virtualAccountId) || personName(detail?.ownerDisplayName ?? null, t);
  const today = todayCivilInZone(me.user.timezone ?? 'UTC');
  const dates = rangeDates(envelopeRange, today);
  // A range the person chose (the URL carries dates); Year to date, the default, carries none.
  const rangeSet = envelopeRange.from !== '' || envelopeRange.to !== '';
  const closeDialog = () => setDialog(null);
  const subscribe = () => setDialog({ kind: 'subscribe' });
  const hasMoveTarget = me.virtualAccounts.some(
    (entry) => entry.mine && entry.id !== virtualAccountId,
  );
  const activeEnvelopes = orderEnvelopes(
    (detail?.envelopes ?? []).filter((entry) => entry.archivedAt === null),
  );

  function selectTab(next: TabKey) {
    setUrl({ tab: next }, { push: true });
  }

  async function handleExport() {
    if (dates === null) return;
    try {
      const { blob, filename } = await envelopesApi.exportXlsx(row.id, dates);
      downloadBlob(blob, filename ?? `${slug(row.name, 'envelope')}.xlsx`);
      notify(t('virtualAccount.downloadStarted'));
    } catch {
      notify(t('virtualAccount.errorGeneric'));
    }
  }

  async function handleMoved(target: MeVirtualAccount, audienceAfter: string[]) {
    await refetchMe();
    const suffix =
      audienceAfter.length > 0
        ? t('envelope.movedVisibleTo', {
            people: new Intl.ListFormat(locale, { type: 'conjunction' }).format(audienceAfter),
          })
        : t('envelope.movedPrivate');
    notify(`${t('envelope.moved', { name: row.name, virtualAccount: target.name })} ${suffix}`);
    void navigate(`/v/${target.id}/e/${row.id}`, { replace: true });
  }

  // The window control with its dates, shared by the Register and P&L toolbars.
  const windowControl = (
    <EnvelopeRangeControl
      value={envelopeRange}
      onChange={(next: EnvelopeRange) => setEnvelopeRange(next)}
      today={today}
    />
  );

  return (
    <>
      <PageHeader
        title={title}
        back={back}
        subtitle={
          <Box component="span" data-envelope-narrative="" sx={{ display: 'block' }}>
            {envelopeNarrative(
              row,
              currency,
              locale,
              t,
              owner ? null : { owner: sponsor, virtualAccount: virtualAccount.name },
            )}
          </Box>
        }
        // The kind only: no privacy pill under the title (TMMAPP-0161 item 3).
        status={
          <TintChip
            tint={ENVELOPE_KIND_TINT[row.kind]}
            label={t(`envelope.kindChip.${row.kind}`)}
          />
        }
        actions={
          <AskAboutThis
            name={t('askAbout.nameAbout', { name: row.name })}
            scope={[{ kind: 'ENVELOPE', id: row.id }]}
          />
        }
        menu={
          <OverflowMenu label={t('envelope.pageActions')} title={row.name} variant="page">
            {(close) => {
              const pick = (action: () => void) => () => {
                close();
                action();
              };
              return [
                canWrite && (
                  <MenuItem key="edit" onClick={pick(() => setDialog({ kind: 'edit' }))}>
                    {t('envelope.menu.edit')}
                  </MenuItem>
                ),
                ownerActions && detail !== null && (
                  <MenuItem key="permissions" onClick={pick(() => setDialog({ kind: 'sharing' }))}>
                    {t('envelope.menu.permissions')}
                  </MenuItem>
                ),
                !owner && (
                  <MenuItem key="shared" onClick={pick(() => setDialog({ kind: 'sharedWithYou' }))}>
                    {t('virtualAccount.menu.sharedWithYou')}
                  </MenuItem>
                ),
                canSeeAlerts(me.tier, owner) && (
                  <MenuItem key="alerts" onClick={pick(() => setDialog({ kind: 'alerts' }))}>
                    {t('alerts.menu')}
                  </MenuItem>
                ),
                owner && row.aiAnalysisAllowed && (
                  <MenuItem key="analyze" onClick={pick(() => setDialog({ kind: 'analyze' }))}>
                    {t('ai.analyze')}
                  </MenuItem>
                ),
                <MenuItem
                  key="download"
                  disabled={dates === null}
                  onClick={pick(() => void handleExport())}
                >
                  {t('envelope.downloadOverview')}
                </MenuItem>,
                canWrite && detail !== null && (
                  <MenuItem key="moveMoney" onClick={pick(() => setDialog({ kind: 'moveMoney' }))}>
                    {t('envelope.menu.moveMoney')}
                  </MenuItem>
                ),
                ownerActions && detail !== null && hasMoveTarget && (
                  <MenuItem key="move" onClick={pick(() => setDialog({ kind: 'move' }))}>
                    {t('envelope.moveTo')}
                  </MenuItem>
                ),
              ];
            }}
          </OverflowMenu>
        }
      />

      <Stack spacing={2.5} sx={{ mt: 2.5 }}>
        <GraceNotice role={role} owner={sponsor} />
        <EnvelopeOverview
          key={version}
          envelope={row}
          virtualAccountId={virtualAccountId}
          currency={currency}
        />
        {hasCategoryTotals(row.allTimeCategoryTotals) && row.allTimeCategoryTotals !== null && (
          <CategoriesInEnvelopeCard
            totals={row.allTimeCategoryTotals}
            currency={currency}
            virtualAccountId={virtualAccountId}
            virtualAccountName={virtualAccount.name}
          />
        )}
        {row.kind === 'BUDGET' && (
          <BudgetHistoryCard
            envelopeId={row.id}
            envelopeName={row.name}
            currency={currency}
            canWrite={canWrite}
            onChanged={load}
            onNotify={notify}
            onSubscriptionRequired={subscribe}
          />
        )}
        {row.notes !== null && row.notes.trim().length > 0 && <NotesCard notes={row.notes} />}
      </Stack>

      <Box sx={{ mt: 2.5 }}>
        <Tabs
          value={tab}
          onChange={(_event, next: TabKey) => selectTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label={t('envelope.tabsLabel')}
          sx={{ mb: 2 }}
        >
          <Tab value="register" label={t('envelope.tabRegister')} />
          <Tab value="recurring" label={t('envelope.tabRecurring')} />
          <Tab value="cashflow" label={t('envelope.tabCashflow')} />
          <Tab value="pnl" label={t('envelope.tabPnl')} />
        </Tabs>
      </Box>

      {tab === 'register' && (
        <RegisterTab
          virtualAccountId={virtualAccountId}
          envelopeId={row.id}
          currency={currency}
          categories={categories}
          canWrite={canWrite}
          owner={owner}
          dates={dates}
          windowControl={windowControl}
          onCategoriesStale={load}
          onNotify={notify}
          onSubscriptionRequired={subscribe}
        />
      )}
      {tab === 'recurring' && (
        <PlanLinesPanel
          envelopeId={row.id}
          envelopeName={row.name}
          currency={currency}
          budget={row.kind === 'BUDGET'}
          canWrite={canWrite}
          onChanged={load}
          onNotify={notify}
          onSubscriptionRequired={subscribe}
        />
      )}
      {tab === 'cashflow' && (
        <EnvelopeForecastTab
          envelope={row}
          currency={currency}
          canWrite={canWrite}
          onNotify={notify}
        />
      )}
      {tab === 'pnl' && (
        <Stack spacing={2}>
          <Paper data-card-header="" sx={{ p: '14px 20px' }}>
            {windowControl}
          </Paper>
          <PnlTab envelopeId={row.id} currency={currency} dates={dates} />
        </Stack>
      )}

      {dialog?.kind === 'analyze' && (
        // The default window (Year to date) sends no dates, so tmm-api analyses the envelope's own
        // budget period (MM-D-40); only a range the person chose is passed on (TMMAPP-0114 A3).
        <AnalysisDialog
          envelopeId={row.id}
          {...(rangeSet && dates?.from !== undefined ? { from: dates.from } : {})}
          {...(rangeSet && dates?.to !== undefined ? { to: dates.to } : {})}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'alerts' && (
        <AlertsDialog
          envelopeId={row.id}
          envelopeName={row.name}
          currency={currency}
          writable={canWriteAlerts(me.tier)}
          onClose={closeDialog}
          onNotify={notify}
        />
      )}
      {dialog?.kind === 'edit' && (
        <EnvelopeFormDialog
          owner={owner}
          virtualAccountId={virtualAccountId}
          currency={currency}
          envelope={row}
          nextSortOrder={nextSortOrder(detail?.envelopes ?? [])}
          onClose={closeDialog}
          onSaved={async () => {
            await load();
            notify(t('envelope.saved'));
          }}
        />
      )}
      {dialog?.kind === 'sharing' && detail !== null && (
        <SharingDialog
          detail={detail}
          readOnly={false}
          // Closing refreshes the page, the shell's list and /me, so who sees it follows
          // (TMMAPP-0158 item 5).
          onClose={() => {
            closeDialog();
            void Promise.all([load(), virtualAccountsList?.reload(), refetchMe()]);
          }}
          // The cards' chips read /me's served names. A failed re-read keeps the shares on screen;
          // on the dialog's open it's the dialog's error (TMMAPP-0160 item 7).
          onChanged={async () => {
            const [detailRead] = await Promise.all([read(), refetchMe()]);
            return detailRead;
          }}
          onNotify={notify}
          onLeft={() => void navigate('/me/virtual-accounts')}
        />
      )}
      {dialog?.kind === 'sharedWithYou' && (
        <InfoDialog
          title={virtualAccount.name}
          body={t('virtualAccount.sharedWithYouBody', { owner: sponsor })}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'moveMoney' && detail !== null && (
        <MoveMoneyDialog
          virtualAccount={detail}
          envelopes={activeEnvelopes}
          initialTo={row.id}
          onClose={closeDialog}
          onMoved={async () => {
            notify(t('virtualAccount.moveMoney.done'));
            await load();
          }}
          onSubscriptionRequired={subscribe}
        />
      )}
      {dialog?.kind === 'move' && detail !== null && (
        <MoveEnvelopeDialog
          envelope={row}
          current={detail}
          onClose={closeDialog}
          onMoved={handleMoved}
        />
      )}
      {dialog?.kind === 'subscribe' && (
        <Dialog open onClose={closeDialog} maxWidth="xs" aria-label={t('subscribe.cardTitle')}>
          <DialogContent>
            <SubscribeCard />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export function EnvelopePage() {
  const params = useParams();
  const virtualAccountId = parseRouteId(params.virtualAccountId);
  const envelopeId = parseRouteId(params.envelopeId);
  if (virtualAccountId === null || envelopeId === null) return <RecordNotFound />;
  return (
    <EnvelopeDetailView
      key={`${virtualAccountId}-${envelopeId}`}
      virtualAccountId={virtualAccountId}
      envelopeId={envelopeId}
    />
  );
}
